import ExternalSignerError from '@ambire-common/classes/ExternalSignerError'
import { URDecoder } from '@ngraveio/bc-ur'
import { translation } from '@web/utils/safeTranslation'

/**
 * The type of a multi part code (`ur:<type>/<seqNum>-<seqLength>/<fragment>`), or null
 * for a single part code (`ur:<type>/<body>`) or anything else.
 */
const getMultiPartType = (fragment: string) => {
  const components = fragment.toLowerCase().slice(3).split('/')
  if (components.length !== 3 || !/^\d+-\d+$/.test(components[1]!)) return null

  return components[0]!
}

export class UrFragmentDecoder {
  private decoder = new URDecoder()

  // Every part the decoder accepted, as scanned (lowercased). The sender keeps showing
  // new parts (fountain codes never repeat a sequence number), so a part not in here is
  // new information, while the camera reading the same code again is not. The whole
  // code is the key, not just its sequence number: a new message starts again at 1.
  private seenParts = new Set<string>()

  private type: string | null = null

  /**
   * Feeds one scanned code. Returns whether it brought anything new: a part the decoder
   * had not seen yet. Scanning the same code again (the scanner reads every code several
   * times, and keeps reading a frozen one) returns false, so callers can tell a transfer
   * that is still moving from one that stalled.
   *
   * When the code belongs to another message of the same type - the sender left the QR
   * codes and showed new ones (new keys, new payload) halfway through the scan - the
   * decoder starts over with the new message instead of rejecting its parts forever.
   */
  add(fragment: string): boolean {
    const part = fragment.toLowerCase()
    const partType = getMultiPartType(part)
    if (partType && this.seenParts.has(part)) return false

    if (this.decoder.receivePart(part)) {
      if (partType) {
        this.seenParts.add(part)
        this.type = this.type || partType
      }
      return true
    }

    if (!partType || this.isComplete() || partType !== this.type) return false

    // Rejected although it is a well formed part of the same type: it belongs to a
    // different message. Only start over when a fresh decoder takes it, so a broken
    // code never throws away what was scanned so far.
    const freshDecoder = new URDecoder()
    if (!freshDecoder.receivePart(part)) return false

    this.decoder = freshDecoder
    this.seenParts = new Set([part])
    return true
  }

  isComplete() {
    return this.decoder.isComplete()
  }

  /**
   * How far an animated (multi part) code got, from 0 to 1: the share of the payload
   * fragments recovered so far. Fountain codes may need a few more parts than the
   * payload splits into, so it can stay put for a moment before the last fragments
   * resolve at once.
   *
   * Not `estimatedPercentComplete()`: that counts every scan, duplicates included, and
   * is capped at 0.99, so it reached "99%" while fragments were still missing just by
   * reading the same codes again - and stayed there when the sender stopped.
   */
  progress(): number {
    if (this.decoder.isComplete()) return 1

    return Math.min(0.99, this.decoder.getProgress())
  }

  result(): Uint8Array {
    const ur = this.decoder.resultUR()

    if (!ur?.cbor) {
      throw new ExternalSignerError(translation.t('Failed to decode UR payload.'))
    }

    return ur.cbor
  }

  reset() {
    this.decoder = new URDecoder()
    this.seenParts = new Set()
    this.type = null
  }
}
